import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../../../../shared/components/Alert/Alert';
import Box from '../../../../shared/components/Box/Box';
import Loader from '../../../../shared/components/Loader/Loader';
import Typography from '../../../../shared/components/Typography/Typography';
import ObjectTabs from '../../../../shared/components/ObjectTabs/ObjectTabs';
import { getAssignableCrmUsers } from '../../crmUsers';
import { getAffair, getAffairScore, updateAffair } from '../affair.api';
import { getPartners } from '../../partners/partner.api';
import { getAllEmployees } from '../../../hr/rh.api';
import { getAffairDisplayName } from '../affairFormatters';
import { affairFromApi } from '../affairModel';
import AffairOverview from '../components/AffairOverview';
import AffairSummaryBand from '../components/AffairSummaryBand';
import AffairContactsPanel from '../components/AffairContactsPanel';
import AffairActivitiesPanel from '../components/AffairActivitiesPanel';
import AffairHistoryPanel from '../components/AffairHistoryPanel';
import AffairDocumentsPanel from '../components/AffairDocumentsPanel';
import AffairMilestonesPanel from '../components/AffairMilestonesPanel';
import AffairPlanningPanel from '../components/AffairPlanningPanel';
import AffairStructurePanel from '../components/AffairStructurePanel';
import AffairQuotesPanel from '../components/AffairQuotesPanel';
import AffairProjectPanel from '../components/AffairProjectPanel';
import AffairEditDialog from '../components/AffairEditDialog';

export const AFFAIR_DETAIL_TABS = [
  { key: 'overview', label: 'Vue d’ensemble' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'activities', label: 'Activités' },
  { key: 'milestones', label: 'Jalons' },
  { key: 'documents', label: 'Documents' },
  { key: 'structure', label: 'Structure' },
  { key: 'planning', label: 'Planning AO / DCE' },
  { key: 'quotes', label: 'Devis' },
  { key: 'project', label: 'Projet / Intervention' },
  { key: 'history', label: 'Historique' },
];

export default function AffairDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = AFFAIR_DETAIL_TABS.some((tab) => tab.key === requestedTab) ? requestedTab : 'overview';
  const [affair, setAffair] = useState(null);
  const [affairScore, setAffairScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [affairData, scoreData] = await Promise.all([getAffair(id), getAffairScore(id)]);
      setAffair(affairFromApi(affairData));
      setAffairScore(scoreData);
      setError('');
    } catch (requestError) {
      setAffair(null);
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  const refreshAfterActivity = async () => {
    const [affairData, scoreData] = await Promise.all([getAffair(id), getAffairScore(id)]);
    setAffair(affairFromApi(affairData));
    setAffairScore(scoreData);
  };
  useEffect(() => { getPartners().then(setPartners).catch(() => setPartners([])); }, []);
  useEffect(() => { getAllEmployees().then((rows) => setUsers(getAssignableCrmUsers(rows))).catch(() => setUsers([])); }, []);

  const saveAffair = async (payload) => {
    try {
      setEditSaving(true); setEditError('');
      await updateAffair(id, payload); await load(); setEditOpen(false);
    } catch (requestError) { setEditError(requestError.message || 'Erreur lors de la modification de l’affaire'); }
    finally { setEditSaving(false); }
  };

  const selectTab = (tabKey) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tabKey === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', tabKey);
    setSearchParams(nextParams, { replace: true });
  };

  const realizations = affair?.realizations || [
    ...(affair?.projects || []),
    ...(affair?.interventions || []),
  ];
  const renderActiveTab = () => {
    if (activeTab === 'overview') return <AffairOverview affair={affair} affairScore={affairScore} />;
    if (activeTab === 'contacts') return <AffairContactsPanel affair={affair} />;
    if (activeTab === 'activities') return <AffairActivitiesPanel affair={affair} onActivityCreated={refreshAfterActivity} />;
    if (activeTab === 'history') return <AffairHistoryPanel affair={affair} />;
    if (activeTab === 'documents') return <AffairDocumentsPanel affair={affair} />;
    if (activeTab === 'structure') return <AffairStructurePanel affair={affair} />;
    if (activeTab === 'planning') return <AffairPlanningPanel affair={affair} />;
    if (activeTab === 'milestones') return <AffairMilestonesPanel affair={affair} users={users} />;
    if (activeTab === 'quotes') return <AffairQuotesPanel affair={affair} />;
    if (activeTab === 'project') return <AffairProjectPanel affair={affair} />;
    return null;
  };

  return (
    <Box className="page-container module-page affair-detail-page">
      <header className="affair-detail-header">
        <div>
          <Link className="affair-detail-back" to="/affaires">← Retour au pipeline</Link>
          <Typography variant="h4">{affair ? getAffairDisplayName(affair) : 'Affaire'}</Typography>
          {affair?.label && <Typography variant="body2" color="textSecondary">{affair.label}</Typography>}
        </div>
        {affair && (
          <div className="affair-detail-header__actions">
            <button className="basic-button" type="button" onClick={() => { setEditError(''); setEditOpen(true); }}>Modifier l’affaire</button>
            <Link className="basic-button" to={`/Devis?affairId=${affair.id}`}>Créer ou voir les devis</Link>
            {realizations.map((item) => {
              const isIntervention = (item.realizationType || item.realization_type) === 'intervention';
              return <Link className="basic-button active" key={`${isIntervention ? 'intervention' : 'project'}-${item.id}`} to={isIntervention ? `/Interventions/${item.id}` : `/projets/${item.id}`}>{isIntervention ? 'Ouvrir l’intervention' : 'Ouvrir le projet'}</Link>;
            })}
          </div>
        )}
      </header>

      {error && <Alert severity="error" action={<button className="basic-button" type="button" onClick={load}>Réessayer</button>}>{error}</Alert>}
      {loading && <Loader label="Chargement de l’affaire..." />}
      {!loading && affair && (
        <>
          <AffairSummaryBand affair={affair} />
          <ObjectTabs tabs={AFFAIR_DETAIL_TABS} activeTab={activeTab} onChange={selectTab} ariaLabel="Sections de l’affaire" />
          <div className="affair-detail-tab-panel" id={`object-panel-${activeTab}`} role="tabpanel" aria-labelledby={`object-tab-${activeTab}`}>
            {renderActiveTab()}
          </div>
        </>
      )}
      {affair && <AffairEditDialog affair={affair} partners={partners} users={users} open={editOpen} loading={editSaving} error={editError} onClose={() => { if (!editSaving) setEditOpen(false); }} onSubmit={saveAffair} />}
    </Box>
  );
}
